/**
 * Home Page
 *
 * Product-first landing page: shows the product catalogue directly.
 * No hero, stats, testimonials, or "why choose us" — just products,
 * with a category filter, sort control, and pagination.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineGrid,
  InlineStack,
  Box,
  Button,
  Spinner,
  Banner,
  Pagination,
  Select,
} from '@shopify/polaris';

// A single product as returned by the /products list endpoint.
interface Product {
  id: string;
  name: string;
  slug: string;
  sku: string;
  primaryImage: string | null;
  startingPrice: number | null;
  samplePrice: number | null;
  isHotSelling: boolean;
  stockStatus: 'IN_STOCK' | 'OUT_OF_STOCK';
  categoryName: string;
}

// A category, used only to build the filter dropdown.
interface Category {
  id: string;
  name: string;
  slug: string;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api/v1';

// How many products to show per page.
const LIMIT = 24;

export function HomePage() {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  // Empty string means "all categories" — no filter applied.
  const [categorySlug, setCategorySlug] = useState('');
  const [sortBy, setSortBy] = useState('newest');

  // Load the category list once, so we can offer a filter dropdown.
  useEffect(() => {
    const controller = new AbortController();

    fetch(`${API_URL}/categories`, { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : Promise.reject()))
      .then((data) => setCategories(data.data || []))
      .catch(() => {
        // A missing category list just means no filter dropdown — not fatal.
      });

    return () => controller.abort();
  }, []);

  // Re-fetch products whenever the page, category, or sort order changes.
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    async function fetchProducts() {
      setLoading(true);
      setError(null);

      try {
        // Only include categorySlug in the query when a category is selected.
        const params = new URLSearchParams({
          page: String(page),
          limit: String(LIMIT),
          sortBy,
        });
        if (categorySlug) params.set('categorySlug', categorySlug);

        const res = await fetch(`${API_URL}/products?${params}`, { signal });
        if (!res.ok) throw new Error('Failed to fetch products');

        const data = await res.json();
        setProducts(data.data || []);
        // meta.total is the full count — divide by page size for total pages.
        setTotalPages(data.meta?.total ? Math.ceil(data.meta.total / LIMIT) : 1);
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        setError('Failed to load products. Please try again.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    }

    fetchProducts();

    return () => controller.abort();
  }, [page, categorySlug, sortBy]);

  // Build the category dropdown options — first entry clears the filter.
  const categoryOptions = [
    { label: 'All categories', value: '' },
    ...categories.map((c) => ({ label: c.name, value: c.slug })),
  ];

  const sortOptions = [
    { label: 'Newest', value: 'newest' },
    { label: 'Price: Low to High', value: 'price_asc' },
    { label: 'Price: High to Low', value: 'price_desc' },
    { label: 'Name', value: 'name' },
  ];

  return (
    <Page title="Products" fullWidth>
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Filter + sort controls */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <InlineStack gap="400" wrap>
                <Box minWidth="200px">
                  <Select
                    label="Category"
                    options={categoryOptions}
                    value={categorySlug}
                    onChange={(value) => {
                      setCategorySlug(value);
                      setPage(1); // reset to first page on a new filter
                    }}
                  />
                </Box>
                <Box minWidth="200px">
                  <Select
                    label="Sort by"
                    options={sortOptions}
                    value={sortBy}
                    onChange={(value) => {
                      setSortBy(value);
                      setPage(1);
                    }}
                  />
                </Box>
              </InlineStack>
            </Box>
          </Card>
        </Layout.Section>

        {/* Product grid */}
        <Layout.Section>
          {loading ? (
            <Box padding="800">
              <BlockStack align="center">
                <Spinner size="large" />
                <Text as="p">Loading products...</Text>
              </BlockStack>
            </Box>
          ) : products.length === 0 ? (
            <Card>
              <Box padding="800">
                <Text as="p" alignment="center">
                  No products found.
                </Text>
              </Box>
            </Card>
          ) : (
            <InlineGrid columns={{ xs: 1, sm: 2, md: 3, lg: 4 }} gap="400">
              {products.map((product) => (
                <Card key={product.id}>
                  <BlockStack gap="200">
                    <Box
                      minHeight="180px"
                      background="bg-surface-secondary"
                      borderRadius="200"
                      position="relative"
                    >
                      {product.primaryImage ? (
                        <img
                          src={product.primaryImage}
                          alt={product.name}
                          loading="lazy"
                          width={200}
                          height={180}
                          style={{
                            width: '100%',
                            height: '180px',
                            objectFit: 'cover',
                            borderRadius: '8px',
                          }}
                        />
                      ) : (
                        <Box
                          minHeight="180px"
                          background="bg-surface-tertiary"
                          borderRadius="200"
                        />
                      )}
                      {product.isHotSelling && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '8px',
                            right: '8px',
                            padding: '4px 8px',
                            backgroundColor: 'var(--p-color-bg-fill-success)',
                            borderRadius: '4px',
                          }}
                        >
                          <Text as="span" variant="bodySm" fontWeight="bold">
                            Hot
                          </Text>
                        </div>
                      )}
                      {/* Out-of-stock products stay visible but are clearly marked,
                          so a customer can still inquire but knows availability. */}
                      {product.stockStatus === 'OUT_OF_STOCK' && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '8px',
                            left: '8px',
                            padding: '4px 8px',
                            backgroundColor: 'var(--p-color-bg-fill-caution)',
                            borderRadius: '4px',
                          }}
                        >
                          <Text as="span" variant="bodySm" fontWeight="bold">
                            Out of Stock
                          </Text>
                        </div>
                      )}
                    </Box>
                    <Box padding="200">
                      <BlockStack gap="100">
                        {/* Reserve a fixed 2-line height for the title so every
                            card lines up. Titles vary from 1 to 3 lines; without
                            this, a longer title pushes the price + button down and
                            the "View Details" buttons no longer align across the row.
                            bodyMd is 13px/20px line-height, so 2 lines = 40px exactly.
                            Longer titles are clamped to 2 lines with an ellipsis. */}
                        <div
                          style={{
                            minHeight: '40px',
                            maxHeight: '40px',
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                          }}
                        >
                          <Text as="h3" variant="bodyMd" fontWeight="semibold">
                            {product.name}
                          </Text>
                        </div>
                        <Text as="p" variant="bodySm" tone="subdued">
                          {product.categoryName}
                        </Text>
                        {/* Show the pricing tier price, or fall back to MRP (samplePrice). */}
                        {/* Ternary (not &&) so a 0/absent price renders nothing, not a bare "0". */}
                        {product.startingPrice || product.samplePrice ? (
                          <Text as="p" variant="bodyMd" fontWeight="bold">
                            Starting at Rs.{product.startingPrice || product.samplePrice}
                          </Text>
                        ) : null}
                        <Button
                          fullWidth
                          onClick={() => navigate(`/products/${product.slug}`)}
                        >
                          View Details
                        </Button>
                      </BlockStack>
                    </Box>
                  </BlockStack>
                </Card>
              ))}
            </InlineGrid>
          )}
        </Layout.Section>

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <Layout.Section>
            <Box padding="400">
              <BlockStack align="center">
                <Pagination
                  hasPrevious={page > 1}
                  hasNext={page < totalPages}
                  onPrevious={() => setPage(page - 1)}
                  onNext={() => setPage(page + 1)}
                />
              </BlockStack>
            </Box>
          </Layout.Section>
        )}
      </Layout>
    </Page>
  );
}
